import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import type { DropdownMenuItem } from '../../components/DropdownMenu';
import RowActionsMenu from '../../components/RowActionsMenu';
import InputField from '../../components/ui/InputField';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import {
    useArchiveProductCategoryMutation,
    useCreateProductCategoryMutation,
    useDeleteProductCategoryMutation,
    useRestoreProductCategoryMutation,
    useUpdateProductCategoryConfigMutation,
    useUpdateProductCategoryMutation,
} from '../../api/productCategory/productCategoryApiSlice';
import type {
    ProductCategory,
    ProductCategoryConfig,
} from '../../api/productCategory/productCategoryApiSlice';
import { accountApiSlice } from '../../api/account/accountApiSlice';
import type { TenantInvoiceIssuer } from '../../api/account/accountApiTypes';
import CategoryConfigForm from './CategoryConfigForm';
import PublicPlanLinks from './PublicPlanLinks';
import { isRoomFamily } from '../../utils/useCases';
import type { InheritedValues, StandardValues } from './categoryConfigRules';
import type { DepositedPrerequisites } from './serviceConfigurationRules';
import {
    groupCategoriesByUseCase,
    isValidCategoryKey,
    suggestCategoryKey,
} from './productCategoryRules';
import { cn } from '../../utils/utils';
import { useIsMaster } from '../../common/useIsMaster';
import { useTenantProductCategoriesQuery } from '../../hooks/useTenantProductCategoriesQuery';
import HardDeleteDialog from '../common/HardDeleteDialog';

interface Props {
    readOnly: boolean;
    /** The tenant's active use cases — the headings categories sit under. */
    services: string[];
    /** What a category of this use case inherits — the standard category's
     *  values, shown on every field left on "inherits" (ADR-0026 §S). */
    inheritedFor: (useCase: string) => InheritedValues;
    /**
     * The standard product category's booking values (ADR-0026 §AF). It is a
     * row of this list like any category (§AE), but its booking fields are
     * the use case's entry, which the caller holds from the account.
     */
    standardFor: (useCase: string) => StandardValues;
    deposited: DepositedPrerequisites;
    invoiceIssuer?: TenantInvoiceIssuer | null;
    /** The tenant's booking page, for each space category's public plan (#3075). */
    bookingPage: { slug: string, active: boolean };
}

/**
 * **Product categories** — what a tenant actually offers, inside each use case
 * (ADR-0026 §F).
 *
 * The use case is a HEADING, not a form. Each use case is one card: its
 * heading, its STANDARD product category first — a real category since
 * ADR-0026 §AE, which every product without a further category belongs to —
 * and any further categories the tenant adds to run the same use case in
 * variants.
 *
 * Every category, standard or further, is edited with the SAME form and the
 * same field set (§AG). On a further category every field is an OVERRIDE of
 * the standard category's value, and "inherits" is the default and a visible
 * choice, shown as the inherited value itself — see `CategoryConfigForm`.
 */
const ProductCategoriesSection = ({
    readOnly,
    services,
    inheritedFor,
    standardFor,
    deposited,
    invoiceIssuer,
    bookingPage,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { data, isLoading, isError, refetch } =
        useTenantProductCategoriesQuery({ includeArchived: true });
    const [createCategory, { isLoading: creating }] = useCreateProductCategoryMutation();
    const [updateCategory, { isLoading: renaming }] = useUpdateProductCategoryMutation();
    const [archiveCategory, { isLoading: archiving }] = useArchiveProductCategoryMutation();
    const [saveConfig, { isLoading: savingConfig }] = useUpdateProductCategoryConfigMutation();
    const [restoreCategory, { isLoading: restoring }] = useRestoreProductCategoryMutation();
    const [deleteCategory] = useDeleteProductCategoryMutation();
    // The hard delete (#2636) is the master's, offered on live and archived
    // rows alike — and even where the tenant is otherwise read-only to them.
    // Never on the standard category, which the server refuses to delete
    // (ADR-0026 §AE).
    const isMaster = useIsMaster();
    const [deleting, setDeleting] = useState<ProductCategory | null>(null);

    /** Which category's configuration is open — null means none. */
    const [configuringId, setConfiguringId] = useState<string | null>(null);

    const handleSaveConfig = async (
        category: ProductCategory,
        config: ProductCategoryConfig,
        name: string,
    ) => {
        // Declaring a category free rewrites its price lists to 0,00 € on the
        // server (ADR-0018 rule 4, ADR-0026 §W) — a write beyond config, so it
        // asks first, naming what it replaces, as the standard category's
        // free switch does.
        // The standard category's charging is its use case's entry (§AF),
        // and a never-saved one has nothing stored yet.
        const standardValues = category.isStandard ? standardFor(category.useCase) : null;
        let storedCharging = category.config?.charging;
        if (standardValues) {
            storedCharging = standardValues.saved ? standardValues.entry.charging : undefined;
        }
        const newlyFree = config.charging === 'free' && storedCharging !== 'free';
        if (newlyFree && !window.confirm(t('services.category_config.free_confirm', {
            name: category.name,
        }))) return;
        try {
            // The settings first: they are the part the server may refuse (a
            // prerequisite, a shared price list). Renaming first would leave a
            // committed new name — and its per-language names dropped — beside
            // settings that were never saved.
            await saveConfig({ id: category.id, config }).unwrap();
            // The standard category's booking fields live in the account's
            // `payments` (ADR-0026 §AF) — a different API slice, so its cache
            // is told explicitly; tags do not cross `createApi` instances.
            if (category.isStandard) {
                dispatch(accountApiSlice.util.invalidateTags(['Account', 'TenantAccount']));
            }
            const trimmed = name.trim();
            if (trimmed && trimmed !== category.name) {
                // A rename replaces the per-language names: otherwise the home
                // tile would keep showing the old title over the new name.
                await updateCategory({
                    id: category.id,
                    name: trimmed,
                    ...(category.langNames ? { langNames: null } : {}),
                }).unwrap();
            }
            dispatch(enqueueSuccessSnackbar({ message: t('services.category_config.saved') }));
            setConfiguringId(null);
        } catch (error) {
            // The server's own reason, not a generic sentence: it says which
            // field it refused and why (#2255/#2258), and "try again" would be
            // advice that can never succeed.
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(error, t('services.category_config.save_error'), t),
                autoHideDuration: 8000,
            }));
        }
    };

    /** Which use case the open create form belongs to — null means closed. */
    const [addingUnder, setAddingUnder] = useState<string | null>(null);
    const [draftName, setDraftName] = useState('');
    const [draftKey, setDraftKey] = useState('');
    // Once the operator edits the key, the name stops driving it. Without
    // this, correcting a typo in the name would silently rewrite a key they
    // had deliberately chosen.
    const [keyTouched, setKeyTouched] = useState(false);

    /**
     * Archived categories are fetched and shown, but they are NOT part of the
     * normal grouping: they take no actions, cannot be configured and must not
     * read as part of the live catalogue. Splitting here keeps every existing
     * render path working on live rows only (#2929).
     */
    const live = useMemo(() => (data ?? []).filter((c) => !c.archivedAt), [data]);
    const archivedByUseCase = useMemo(() => {
        const out = new Map<string, ProductCategory[]>();
        for (const category of data ?? []) {
            if (!category.archivedAt) continue;
            const key = category.useCase;
            out.set(key, [...(out.get(key) ?? []), category]);
        }
        return out;
    }, [data]);

    const groups = useMemo(() => {
        const fromLive = groupCategoriesByUseCase(live, services);

        // A use case the tenant no longer runs gets an "orphaned" group only
        // because one of its categories put it there — and `groupCategories-
        // ByUseCase` builds that from the list it is given, which is now the
        // LIVE one. So a use case that is switched off AND whose categories
        // are all archived would produce no group at all, and its rows would
        // be exactly as invisible as before this change.
        //
        // That is not a corner: it is the sequence that produced #2929 — a
        // category archived, then the use case switched off.
        const seen = new Set(fromLive.map((group) => group.useCase));
        const archivedOnly = Array.from(archivedByUseCase.keys())
            .filter((useCase) => !seen.has(useCase))
            .map((useCase) => ({
                useCase,
                categories: [],
                orphaned: !services.includes(useCase),
            }));

        return [...fromLive, ...archivedOnly];
    }, [live, services, archivedByUseCase]);

    const closeCreate = () => {
        setAddingUnder(null);
        setDraftName('');
        setDraftKey('');
        setKeyTouched(false);
    };

    const openCreate = (useCase: string) => {
        closeCreate();
        setAddingUnder(useCase);
    };

    const handleNameChange = (value: string) => {
        setDraftName(value);
        if (!keyTouched) setDraftKey(suggestCategoryKey(value));
    };

    const handleCreate = async () => {
        if (!addingUnder) return;
        try {
            await createCategory({
                key: draftKey.trim(),
                name: draftName.trim(),
                useCase: addingUnder,
            }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('services.product_categories.created') }));
            closeCreate();
        } catch (error) {
            // The server's own message, not a generic one: it distinguishes a
            // duplicate key (409, possibly an archived one) from a malformed
            // key (400), and those need different corrections.
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(error, t('services.product_categories.save_error'), t),
                autoHideDuration: 8000,
            }));
        }
    };

    const handleArchive = async (category: ProductCategory) => {
        if (!window.confirm(t('services.product_categories.archive_confirm', {
            name: category.name,
        }))) return;
        try {
            const result = await archiveCategory(category.id).unwrap();
            dispatch(enqueueSuccessSnackbar({
                message: t('services.product_categories.archived', {
                    count: result?.productsArchived ?? 0,
                }),
            }));
        } catch (error) {
            // A category with live products is refused WITH the count, so the
            // operator learns what to move first rather than that it "did not
            // work".
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(error, t('services.product_categories.save_error'), t),
                autoHideDuration: 8000,
            }));
        }
    };

    const busy = creating || renaming || archiving || savingConfig || restoring;

    const handleRestore = async (category: ProductCategory) => {
        try {
            await restoreCategory(category.id).unwrap();
            dispatch(enqueueSuccessSnackbar({
                message: t('services.product_categories.restored'),
            }));
        } catch (error) {
            // The server's own words, like every other action on this screen:
            // a restore can fail for reasons only it knows, and "did not work"
            // sends the operator nowhere.
            dispatch(enqueueErrorSnackbar({
                message: errorMessageFrom(
                    error,
                    t('services.product_categories.restore_failed'),
                    t,
                ),
                autoHideDuration: 8000,
            }));
        }
    };

    // The hard delete is the master's (#2636) and always sits last in the
    // row's menu, behind a divider (design-decisions.md › Zeilenaktionen in
    // Tabellen).
    const deleteItem = (category: ProductCategory): DropdownMenuItem => ({
        id: 'delete',
        label: t('services.product_categories.delete.action'),
        onClick: () => setDeleting(category),
        disabled: busy,
        hidden: !isMaster,
    });

    /**
     * An archived category: its NAME and its KEY, and one way back.
     *
     * No edit, no configuration, no archive — it is out of the catalogue, and
     * offering actions on it would suggest otherwise. The key is the reason
     * this row has to exist at all: keys are never reused, so an operator who
     * cannot see this row is told a key is taken and has no way to find out by
     * what (#2929).
     */
    const renderArchivedCategory = (category: ProductCategory) => (
        <div
            key={category.id}
            className={'border-b border-foreground/5 last:border-0'}
        >
            <div className={'flex flex-wrap items-center gap-3 py-2'}>
                <div className={'flex-grow basis-48 min-w-0'}>
                    <p className={'text-sm text-foreground/60 truncate'}>
                        {category.name}
                    </p>
                    <p className={'text-xs text-foreground/40 font-mono'}>
                        {category.key}
                    </p>
                </div>
                <div className={'shrink-0'}>
                    <RowActionsMenu
                        rowName={category.name}
                        items={[{
                            id: 'restore',
                            label: t('services.product_categories.restore'),
                            onClick: () => handleRestore(category),
                            disabled: busy,
                            hidden: readOnly,
                        }]}
                        destructiveItems={[deleteItem(category)]}
                    />
                </div>
            </div>
        </div>
    );

    const renderCategory = (category: ProductCategory) => (
        <div key={category.id} className={'border-b border-foreground/5 last:border-0'}>
            {/* #2886: wraps, because it did not. The name column carries
            `min-w-0` so it can shrink — and with non-shrinking buttons beside
            it on a narrow viewport it shrank to width ZERO, leaving the
            category name invisible. `basis-48` gives the name a floor, and the
            button group takes its own line rather than pushing. */}
            <div className={'flex flex-wrap items-center gap-3 py-2'}>
                <div className={'flex-grow basis-48 min-w-0'}>
                    <p className={'text-sm text-foreground truncate'}>{category.name}</p>
                    {category.isStandard && (
                        <p className={'text-xs text-foreground/60'}>
                            {t('services.product_categories.standard_desc')}
                        </p>
                    )}
                    {/* The key is shown, not hidden: it is what tenant
                    configuration points at, it cannot be changed afterwards,
                    and an operator comparing environments needs to read it. */}
                    <p className={'text-xs text-foreground/50 font-mono'}>{category.key}</p>
                    {/* #3075: a space category's rooms as a plan for the
                        tenant's website. Gated on the space template, not on
                        a module of its own: the plan IS the room week view,
                        which every space category has, and the room list
                        behind it accepts exactly these categories
                        (ROOM_USE_CASES in new-backend/app
                        categoryRoomsController.ts). */}
                    {isRoomFamily(category.useCase) && (
                        <PublicPlanLinks
                            categoryKey={category.key}
                            categoryName={category.name}
                            slug={bookingPage.slug}
                            active={bookingPage.active}
                        />
                    )}
                </div>
                {/* ONE edit action for everything about a category — its name
                    and its whole booking flow (PO, 2026-10-01: a separate
                    "Bearbeiten" for the name beside "Buchungsflow" for the rest
                    read as two different things). Every action here changes
                    something, so all sit in the row's menu (design-decisions.md
                    › Zeilenaktionen in Tabellen). The standard category is never
                    archived or deleted (§AE). */}
                <div className={'shrink-0'}>
                    <RowActionsMenu
                        rowName={category.name}
                        items={[
                            {
                                id: 'edit',
                                label: t('common.edit'),
                                onClick: () => setConfiguringId(
                                    configuringId === category.id ? null : category.id,
                                ),
                                disabled: busy,
                                hidden: readOnly,
                            },
                            {
                                id: 'archive',
                                label: t('services.product_categories.archive'),
                                onClick: () => handleArchive(category),
                                disabled: busy,
                                hidden: readOnly || category.isStandard,
                            },
                        ]}
                        destructiveItems={category.isStandard ? [] : [deleteItem(category)]}
                    />
                </div>
            </div>

            {configuringId === category.id && !readOnly && (
                <CategoryConfigForm
                    category={category}
                    inherited={inheritedFor(category.useCase)}
                    {...(category.isStandard ? { standard: standardFor(category.useCase) } : {})}
                    deposited={deposited}
                    invoiceIssuer={invoiceIssuer}
                    saving={savingConfig || renaming}
                    onCancel={() => setConfiguringId(null)}
                    onSave={(config, name) => handleSaveConfig(category, config, name)}
                />
            )}
        </div>
    );

    const renderCreateForm = () => (
        <div className={'mt-3 flex flex-col gap-3 rounded-[5px] border border-foreground/10 p-3'}>
            <InputField
                label={t('services.product_categories.name')}
                labelAbove={true}
                value={draftName}
                onChange={handleNameChange}
                disabled={creating}
            />
            <InputField
                label={t('services.product_categories.key')}
                labelAbove={true}
                help={t('services.product_categories.key_help')}
                value={draftKey}
                onChange={(value) => {
                    setKeyTouched(true);
                    setDraftKey(value);
                }}
                disabled={creating}
                error={draftKey.length > 0 && !isValidCategoryKey(draftKey)}
                errorText={t('services.product_categories.key_invalid')}
            />
            <div className={'flex justify-end gap-2'}>
                <CustomButton
                    title={t('common.cancel')}
                    onClick={closeCreate}
                    isFilled={false}
                />
                <CustomButton
                    title={t('common.save')}
                    onClick={handleCreate}
                    isFilled={true}
                    loading={creating}
                    disabled={!draftName.trim() || !isValidCategoryKey(draftKey)}
                />
            </div>
        </div>
    );

    // One card per use case (PO, 2026-08-19: the boundaries between use cases
    // must be unmistakable). A failed load is never rendered as "no
    // categories", or the operator would create duplicates of ones that
    // already exist.
    const card = 'bg-text-contrast rounded-[5px] shadow-sm p-6';

    if (isLoading) {
        return (
            <section id={'product-categories'} className={card}>
                <div className={'flex justify-center py-6'}>
                    <div
                        className={'animate-spin rounded-full h-8 w-8 border-b-2 border-primary'}
                    />
                </div>
            </section>
        );
    }

    return (
        <div id={'product-categories'} className={'flex flex-col gap-6'}>
            {/* A failed load says so, with a retry. Adding is withheld until
                the list is back, so nothing is created twice. */}
            {isError && (
                <section className={card}>
                    <div className={'flex items-center gap-3'}>
                        <p className={'text-sm text-foreground/60'}>
                            {t('services.product_categories.load_error')}
                        </p>
                        <CustomButton
                            title={t('services.product_categories.retry')}
                            onClick={() => refetch()}
                            isFilled={false}
                        />
                    </div>
                </section>
            )}
            {groups.map((group) => (
                <section key={group.useCase} className={card}>
                    {/* The use case is the heading — not a form (ADR-0026 §F). */}
                    <h3 className={'text-sm font-semibold text-foreground mb-3'}>
                        {t(`service_type_selector.${group.useCase}`, {
                            defaultValue: group.useCase,
                        })}
                    </h3>

                    {/* Shown rather than hidden: these categories still
                        exist, still hold products and are still pointed
                        at by configuration. Dropping them would read as
                        "there are none". */}
                    {group.orphaned && (
                        <p className={'text-xs text-foreground/60 mb-2'}>
                            {t('services.product_categories.use_case_off')}
                        </p>
                    )}

                    {group.categories.length === 0 ? (
                        /* "None yet" only for a switched-off use case with
                           genuinely nothing — an active one always has its
                           standard category, and one whose categories are all
                           archived has some. */
                        group.orphaned
                            && (archivedByUseCase.get(group.useCase) ?? []).length === 0 && (
                            <p className={'text-sm text-foreground/40 italic py-2'}>
                                {t('services.product_categories.none_yet')}
                            </p>
                        )
                    ) : (
                        <div className={cn(group.orphaned && 'opacity-70')}>
                            {group.categories.map(renderCategory)}
                        </div>
                    )}

                    {(archivedByUseCase.get(group.useCase) ?? []).length > 0 && (
                        <div className={'mt-3'}>
                            <p className={'text-xs text-foreground/50 mb-1'}>
                                {t('services.product_categories.archived_heading')}
                            </p>
                            {(archivedByUseCase.get(group.useCase) ?? [])
                                .map(renderArchivedCategory)}
                        </div>
                    )}

                    {!readOnly && !group.orphaned && !isError && (
                        addingUnder === group.useCase ? renderCreateForm() : (
                            <div className={'mt-3'}>
                                <CustomButton
                                    title={t('services.product_categories.add')}
                                    onClick={() => openCreate(group.useCase)}
                                    disabled={busy}
                                    isFilled={false}
                                />
                            </div>
                        )
                    )}
                </section>
            ))}

            {deleting && (
                <HardDeleteDialog
                    key={deleting.id}
                    name={deleting.name}
                    i18nPrefix={'services.product_categories.delete'}
                    run={() => deleteCategory(deleting.id).unwrap()}
                    onClose={() => setDeleting(null)}
                />
            )}
        </div>
    );
};

export default ProductCategoriesSection;
